// mockApi — implements the oneselfpath API contract against the in-memory state in
// state.ts. Mirrors the retired backend's behaviour where it matters to the screens
// (status cascades, bookmarks, notifications on path events).
import type { Notification, PathSession, Prompt } from "@/api/types";
import type { OneselfPathApi } from "../oneselfpath-api";
import { ApiError } from "../api";
import { getState, paginate } from "./state";
import { IS_MOCK_AUTH, getFirebaseAuth } from "../firebase";

/** State for the signed-in patient (by email); mock auth and unknown emails get the demo patient. */
const st = () => getState(IS_MOCK_AUTH ? undefined : getFirebaseAuth().currentUser?.email);

const latency = (ms = 120) => new Promise((r) => setTimeout(r, ms));
const notFound = (what: string) => new ApiError(404, `${what} not found`);
const today = () => new Date().toISOString().slice(0, 10);

function pathFor(session: PathSession) {
  return st().paths.find((p) => p.id === session.path);
}

/** Mirror the old cascade: a session leaving "new" starts the path; all completed completes it. */
function cascade(session: PathSession) {
  const path = pathFor(session);
  if (!path) return;
  const own = st().sessions.filter((s) => s.path === path.id);
  if (own.every((s) => s.status === "completed")) {
    if (path.status !== "completed") pushNotification("Path completed", `You completed the ${path.name} path.`);
    path.status = "completed";
    path.end_date = today();
  } else if (path.status === "new" && session.status && session.status !== "new") {
    path.status = "in_progress";
    path.start_date = today();
  }
  st().refreshPath(path);
}

function pushNotification(title: string, message: string) {
  const n: Notification = {
    id: Math.max(0, ...st().notifications.map((x) => x.id)) + 1,
    uuid: crypto.randomUUID(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: null,
    updated_by: null,
    user: st().USER_ID,
    title,
    message,
    is_read: false,
    is_sent: true,
  };
  st().notifications.unshift(n);
}

export const mockApi: OneselfPathApi = {
  // --- me
  async me() {
    await latency();
    return st().user;
  },
  async updateMe(data) {
    await latency();
    Object.assign(st().user, data, { updated_at: new Date().toISOString() });
    return st().user;
  },
  async updateProfile(data) {
    return mockApi.updateMe(data);
  },
  async termsAccepted() {
    await latency();
    return { terms_and_conditions_accepted: st().user.terms_and_conditions_accepted };
  },
  async dashboard() {
    await latency();
    const started = st().paths.filter((p) => p.status !== "new").length;
    const completed = st().paths.filter((p) => p.status === "completed").length;
    const waypoints = st().sessions.filter((s) => s.status === "completed").length;
    const progress = st().paths.length ? Math.round(st().paths.reduce((a, p) => a + p.progress, 0) / st().paths.length) : 0;
    const minutes = Math.round(waypoints * 8.5);
    return {
      path_started: started,
      path_completed: completed,
      waypoints_completed: waypoints,
      joined_date: st().user.date_joined ?? st().user.created_at,
      last_update_date: new Date().toISOString(),
      overall_progress: progress,
      total_time_spent: `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}:00`,
      notifications: st().notifications.slice(0, 3),
    };
  },
  async preferences() {
    await latency();
    return st().preferences;
  },
  async updatePreferences(data) {
    await latency();
    Object.assign(st().preferences, data, {
      notifications: { ...st().preferences.notifications, ...(data.notifications ?? {}) },
    });
    return st().preferences;
  },
  async checkHealthData() {
    await latency(1500);
    st().user.is_onboarded = true;
    pushNotification("Health data checked", "We looked at your latest records. No new paths were needed this time.");
    return { detail: "Health data processed" };
  },
  async syncHealthData() {
    await latency(600);
    return { detail: "Health data sync started" };
  },
  async deactivate() {
    await latency();
    st().user.deactivated_at = new Date().toISOString();
    return { detail: "Account deactivated" };
  },

  // --- paths
  async paths(params) {
    await latency();
    const rows = st().paths.filter((p) => !params?.status || p.status === params.status).map(st().refreshPath);
    return paginate(rows, params);
  },
  async path(id) {
    await latency();
    const p = st().paths.find((x) => x.id === id);
    if (!p) throw notFound("Path");
    return st().refreshPath(p);
  },
  async startPath(id) {
    await latency();
    const p = st().paths.find((x) => x.id === id);
    if (!p) throw notFound("Path");
    p.status = "in_progress";
    p.start_date = today();
    st().sessions
      .filter((s) => s.path === p.id)
      .forEach((s, i) => {
        if (s.status === "new") s.status = i === 0 ? "current" : "in_progress";
      });
    pushNotification("Path started", `You started the ${p.name} path.`);
    return st().refreshPath(p);
  },
  async endPath(id) {
    await latency();
    const p = st().paths.find((x) => x.id === id);
    if (!p) throw notFound("Path");
    p.status = "completed";
    p.end_date = today();
    st().sessions.filter((s) => s.path === p.id).forEach((s) => (s.status = "completed"));
    return st().refreshPath(p);
  },
  async updateSession(id, data) {
    await latency();
    const s = st().sessions.find((x) => x.id === id);
    if (!s) throw notFound("Session");
    const { recommendations, ...rest } = data as Partial<PathSession> & { recommendations?: (Prompt | string)[] };
    Object.assign(s, rest, { updated_at: new Date().toISOString() });
    if (Array.isArray(recommendations)) {
      s.recommendations = recommendations.map((r, i) =>
        typeof r === "string"
          ? { ...(s.recommendations[i] ?? { prompt_id: `p-${i}`, is_bookmarked: false, rating: 0 }), prompt_text: r }
          : { ...(s.recommendations.find((x) => x.prompt_id === r.prompt_id) ?? {}), ...r },
      );
    }
    cascade(s);
    return s;
  },
  async bookmarkedSessions(pathId) {
    await latency();
    return st().sessions.filter((s) => s.path === pathId && (s.is_bookmarked || s.recommendations.some((r) => r.is_bookmarked)));
  },
  async allBookmarkedSessions(params) {
    await latency();
    return paginate(
      st().sessions.filter((s) => s.is_bookmarked || s.recommendations.some((r) => r.is_bookmarked)),
      params,
    );
  },

  // --- notifications
  async notifications(params) {
    await latency();
    return paginate([...st().notifications].sort((a, b) => b.created_at.localeCompare(a.created_at)), params);
  },
  async updateNotification(id, data) {
    await latency();
    const n = st().notifications.find((x) => x.id === id);
    if (!n) throw notFound("Notification");
    Object.assign(n, data, { updated_at: new Date().toISOString() });
    return n;
  },
  async deleteNotification(id) {
    await latency();
    const i = st().notifications.findIndex((x) => x.id === id);
    if (i >= 0) st().notifications.splice(i, 1);
  },

  // --- PathFinder
  async questionSuggestions(params) {
    await latency();
    const q = (params?.search ?? "").trim().toLowerCase();
    return st().questionSuggestions
      .filter((s) => s.is_active)
      .filter((s) => !q || s.keywords.toLowerCase().includes(q) || s.question.toLowerCase().includes(q))
      .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  },
  async recentSearches() {
    await latency();
    return { recent_searches: st().recentSearches };
  },
  async deleteRecentSearch(id) {
    await latency();
    const i = st().recentSearches.findIndex((x) => String(x.id) === String(id));
    if (i >= 0) st().recentSearches.splice(i, 1);
  },
  async clearRecentSearches() {
    await latency();
    st().recentSearches.splice(0);
  },
  async trackSelection(data) {
    await latency();
    const q = st().questionSuggestions.find((s) => s.id === data.question_id) ?? null;
    st().recentSearches.unshift({
      id: Math.max(0, ...st().recentSearches.map((r) => r.id)) + 1,
      search_keyword: data.search_keyword || q?.question || "",
      selected_question: q,
      created_at: new Date().toISOString(),
    });
    return { detail: "Selection tracked" };
  },

  // --- health summary
  async conditions(params) {
    await latency();
    return paginate(st().conditions, params);
  },
  async encounters(params) {
    await latency();
    return paginate(st().encounters, params);
  },
  async observations(params) {
    await latency();
    return paginate(st().observations, params);
  },
  async medicationRequests(params) {
    await latency();
    return paginate(st().medicationRequests, params);
  },
  async medicationAdministrations(params) {
    await latency();
    return paginate(st().medicationAdministrations, params);
  },
  async procedures(params) {
    await latency();
    return paginate(st().procedures, params);
  },
  async serviceRequests(params) {
    await latency();
    return paginate(st().serviceRequests, params);
  },
  async practitioners(params) {
    await latency();
    return paginate(st().practitioners, params);
  },

  // --- sign-up profile registration (credentials are Firebase's)
  async signUp(data) {
    await latency(600);
    Object.assign(st().user, {
      email: data.email,
      first_name: data.first_name ?? st().user.first_name,
      middle_name: data.middle_name ?? null,
      last_name: data.last_name ?? st().user.last_name,
      date_of_birth: data.date_of_birth ?? st().user.date_of_birth,
      phone_number: data.phone_number ?? "",
      address: data.address ?? {},
      terms_and_conditions_accepted: data.terms_and_conditions_accepted ?? false,
      is_onboarded: false,
      has_seen_welcome_tour: false,
    });
    return st().user;
  },
};
